// Client orchestration of the account-linking flows — thin over brokeredLogin
// and the identity server's client, so the UI calls one named intention per
// gesture. The flows are drawn in the account lifecycle resource of the identity ADR
// (canon/auto-auction-com/architecture/resources/identity-and-authentication/account-lifecycle-and-linking.md).

import { brokeredLogin } from './brokeredLogin.js';
import { identityServerClient } from './identityServerClient.js';
import { clearPendingLogin, getPendingLoginToken, parkPendingLogin } from './pendingLogin.js';
import { clearSession, setSession } from './sessionTokenStorage.js';

// Sign-in & security → "Add a login": link-mode login (prompt=login);
// the return trip is completed by completeBootAuth's link branch.
export async function startLink(providerName, targetPath = '/') {
  return brokeredLogin.beginLinkLogin(providerName, targetPath);
}

// The ACCOUNT_NOT_LINKED choice screen's "I already have an account": park
// the just-proven login as the thing to attach and send the person through
// sign-in with the account they already use. When that login resolves,
// completeBootAuth attaches the parked login.
//
// `prompt: 'login'` is essential: the provider's session from the login that
// just landed here is still live, so a plain login would silently re-SSO the
// SAME identity and bounce the person straight back to this screen. Fresh auth
// shows the hosted login so they can sign in with the DIFFERENT account they
// mean to attach to.
export async function connectToExistingAccount() {
  parkPendingLogin();
  await clearSession();
  return brokeredLogin.beginLogin('/', { prompt: 'login' });
}

// Each of the three choices below presents the pending-account token to the
// identity server and gets a session back. That is the swap: the session's
// tokens are stored, the pending token — which the server has already made
// worthless — is dropped, and a plain hydrate then signs the person in.
async function openSessionWith(choice) {
  const tokens = await choice(getPendingLoginToken());
  await setSession(tokens);
  clearPendingLogin();
  return tokens;
}

// The choice screen's "Create a new account": the thin create.
export async function createNewAccount() {
  return openSessionWith(identityServerClient.createAccount);
}

// The MATCH_FOUND screen's "Connect and continue": attach the just-proven login
// to the account its verified email already anchors.
export async function connectMatchedAccount() {
  return openSessionWith(identityServerClient.connectMatch);
}

// The restore screen's "Restore my account".
export async function restoreClosedAccount() {
  return openSessionWith(identityServerClient.restoreAccount);
}
